/**
 * The typed client for the Express API in `server/src/api.ts`.
 *
 * One place that knows about `fetch`, one place that knows the response
 * shapes. Nothing here re-derives a rule -- statuses a cell may move to, note
 * counts, milestone order and the current+previous window all arrive from the
 * server already decided, because they are the same rules the Phase 2 modules
 * enforce and a second copy in the browser would be a second thing to get wrong.
 */

/** An API error the UI can show verbatim -- the server writes them for people. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    // The session is an httpOnly cookie, so it has to ride along. Same-origin
    // is fetch's default, but stating it makes the dependency visible -- and
    // this app is deliberately served from one origin so it stays same-origin.
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', ...init?.headers },
  })

  if (!response.ok) {
    let message = `Request failed (${response.status})`
    try {
      const body = (await response.json()) as { error?: string }
      if (body.error) message = body.error
    } catch {
      // A non-JSON error body -- the status line is all we have.
    }
    throw new ApiError(response.status, message)
  }

  return (await response.json()) as T
}

const query = (params: Record<string, string | null | undefined>): string => {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') search.set(key, value)
  }
  const qs = search.toString()
  return qs === '' ? '' : `?${qs}`
}

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

/**
 * Who is signed in, or why nobody is.
 *
 * `anonymous` is not an error state: it is a server with no Cognito configured,
 * which is how EQ Practice runs on the practice's own machine and how it ran
 * for all of Phases 1-4. The client shows the difference rather than hiding it.
 */
export interface AuthState {
  mode: 'cognito' | 'anonymous'
  reason?: string
  user: {
    id: string
    email: string
    name: string
    role: 'staff' | 'manager'
    isActive: boolean
    /** R18: other products this person holds a live grant for. */
    otherProducts?: string[]
  } | null
}

/** D3: who a period or obligation is allocated to, resolved on the server. */
export interface Allocation {
  /** null only for 'unmatched' -- imported initials nobody holds. */
  userId: string | null
  name: string
  source: 'period' | 'obligation' | 'initials' | 'unmatched'
}

export interface StaffMember {
  id: string
  name: string
  email: string
  initials: string | null
  role: 'staff' | 'manager'
}

export interface ObligationTypeInUse {
  code: string
  description: string
  clientCount: number
}

export interface ReportItem {
  itemType: 'obligation' | 'job'
  itemId: string
  clientId: string
  clientName: string
  itemCode: string
  itemDescription: string
  dueDate: string
  status: string
  assignedTo: string | null
  allocation: Allocation | null
  /** Positive = overdue, negative = not yet due. */
  daysOverdue: number
  isOverdue: boolean
  nextStatuses: string[]
}

export interface DueAndOverdueResponse {
  asOfDate: string
  overdueOnly: boolean
  withinDays: number | null
  allocatedTo: string | null
  dueBefore: string | null
  staff: StaffMember[]
  /** Of the whole report, not the filtered view -- both filter links show one. */
  totalCount: number
  overdueCount: number
  items: ReportItem[]
}

export interface MilestoneProgress {
  key: string
  label: string
  date: string | null
  /** Exactly one milestone per cell is eligible to be ticked next. */
  isNext: boolean
}

export interface MatrixCell {
  itemId: string
  dueDate: string | null
  status: string
  assignedTo: string | null
  allocation: Allocation | null
  hasAllocationOverride: boolean
  forReviewDate: string | null
  completedDate: string | null
  /** D7: set only while the status is not_applicable. */
  notApplicableReason: string | null
  notApplicableDate: string | null
  nextStatuses: string[]
  noteCount: number
  /** COIDA_RETURN only; null everywhere else. */
  coidaMilestones: MilestoneProgress[] | null
}

export interface MatrixRow {
  clientId: string
  clientName: string
  referenceNumber: string | null
  assignedTo: string | null
  obligationId: string
  allocation: Allocation | null
  cells: Record<string, MatrixCell>
}

export interface MatrixPeriod {
  key: string
  periodStart: string
  periodEnd: string
}

export interface CategoryTab {
  key: string
  label: string
  active: boolean
}

export interface MatrixResponse {
  code: string
  description: string
  isCoida: boolean
  referenceColumn: { field: string; label: string } | null
  categoryTabs: CategoryTab[] | null
  assignedToOptions: string[]
  staff: StaffMember[]
  statusOptions: string[]
  periods: MatrixPeriod[]
  rows: MatrixRow[]
  filters: {
    category: string | null
    search: string
    assignedTo: string
    allocatedTo: string
    status: string
    periods: 'recent' | 'all'
    asOfDate: string
  }
  rawHasRows: boolean
}

export interface Note {
  id: string
  author: string
  body: string
  createdAt: string
}

export interface NotesResponse {
  clientName: string
  itemCode: string
  periodStart: string
  periodEnd: string
  notes: Note[]
}

/** The client register. */

export type ClientStatus = 'active' | 'dormant' | 'resigned'

export type EntityType =
  | 'individual' | 'company' | 'cc' | 'trust'
  | 'partnership' | 'deceased_estate' | 'sole_proprietor' | 'npo'

export interface ClientListRow {
  id: string
  clientNumber: number
  shortName: string
  registeredName: string | null
  entityType: EntityType | null
  status: ClientStatus
  groupName: string | null
  groupNumber: string | null
  registrations: string[]
  obligationCount: number
  tradingName: string | null
  registrationNumber: string | null
  /** null = not yet captured, which is not the same as "no". */
  vatRegistered: boolean | null
  vatNumber: string | null
  vatCategoryCode: string | null
  financialYearEndMonth: number | null
  lastChangedAt: string | null
}

export interface ClientListResponse {
  total: number
  counts: { active: number; dormant: number; resigned: number }
  clients: ClientListRow[]
}

/**
 * One client in full.
 *
 * `clientNumber` and `smartpracticeCustomerId` are present so the detail view
 * can SHOW them, and absent from every write path -- see ClientPatch.
 */
export interface ClientDetail {
  id: string
  clientNumber: number
  smartpracticeCustomerId: string | null
  shortName: string
  registeredName: string | null
  tradingName: string | null
  entityType: EntityType | null
  entityTypeSourceHint: string | null
  status: ClientStatus
  shelfNumber: number | null
  registrationNumber: string | null
  /** An individual's SA ID number, 13 digits. */
  idNumber: string | null
  /** Worked out by the server from `idNumber` -- the only fact read from it. */
  idNumberBirthDate: string | null
  idNumberAge: number | null
  incomeTaxNumber: string | null
  vatNumber: string | null
  payeNumber: string | null
  uifNumber: string | null
  coidaNumber: string | null
  financialYearEndMonth: number | null
  vatFilingPeriodStart: number | null
  vatCategoryCode: string | null
  dateOnboarded: string | null
  groupId: string | null
  vatRegistered: boolean | null
  vatRegisteredFrom: string | null
  uifRefSars: string | null
  sdlNumber: string | null
  sicCode: string | null
  tradeClassification: string | null
  physUnit: string | null
  physComplex: string | null
  physStreetNo: string | null
  physStreetName: string | null
  physSuburb: string | null
  physCity: string | null
  physPostalCode: string | null
  physCountry: string | null
  postalSameAsPhysical: boolean
  postalLine1: string | null
  postalLine2: string | null
  postalLine3: string | null
  postalPostalCode: string | null
  group: { id: string; name: string; groupNumber: string | null } | null
  vatCategory: { code: string; description: string } | null
  contacts: {
    id: string
    role: string | null
    isPrimary: boolean
    contact: { fullName: string; email: string | null; phone: string | null }
  }[]
  partnerUserId: string | null
  partner: { id: string; fullName: string | null; email: string } | null
  _count: { obligations: number; jobs: number; documents: number; notes: number }
}

/** One obligation a client carries (P0-5). */
export interface ClientObligation {
  id: string
  obligationTypeCode: string
  description: string
  typeIsActive: boolean
  activeFrom: string
  activeTo: string | null
  assignedUserId: string | null
  assignedUserName: string | null
  assignedTo: string | null
  initialsResolveTo: string | null
  periodCount: number
  openPeriodCount: number
  undatedOpenPeriodCount: number
}

export interface Refusal {
  obligationId: string
  clientId: string
  clientName: string
  obligationTypeCode: string
  stage: 'generate' | 'due_date'
  periodId: string | null
  reason: string
}

export interface BulkResult {
  status: 'completed' | 'not_applicable'
  requested: number
  changed: number
  reviewOverridden: number
  skipped: { periodId: string; reason: string }[]
}

export interface EnsureResult {
  years: number[]
  obligationsChecked: number
  periodsCreated: number
  dueDatesSet: number
  refusals: Refusal[]
}

/**
 * What may be changed.
 *
 * `clientNumber` is deliberately not in this type. It is immutable -- assigned
 * once at onboarding, never reused, and the client's Accounts ledger is named
 * after it. The server refuses it and a database trigger refuses it again.
 */
export interface ClientPatch {
  shortName?: string
  registeredName?: string | null
  tradingName?: string | null
  entityType?: EntityType | null
  status?: ClientStatus
  groupId?: string | null
  registrationNumber?: string | null
  idNumber?: string | null
  incomeTaxNumber?: string | null
  vatNumber?: string | null
  payeNumber?: string | null
  uifNumber?: string | null
  coidaNumber?: string | null
  financialYearEndMonth?: number | null
  vatFilingPeriodStart?: number | null
  vatCategoryCode?: string | null
  shelfNumber?: number | null
  vatRegistered?: boolean | null
  /** 'YYYY-MM-DD' */
  vatRegisteredFrom?: string | null
  uifRefSars?: string | null
  sdlNumber?: string | null
  sicCode?: string | null
  tradeClassification?: string | null
  physUnit?: string | null
  physComplex?: string | null
  physStreetNo?: string | null
  physStreetName?: string | null
  physSuburb?: string | null
  physCity?: string | null
  physPostalCode?: string | null
  physCountry?: string | null
  postalSameAsPhysical?: boolean
  postalLine1?: string | null
  postalLine2?: string | null
  postalLine3?: string | null
  postalPostalCode?: string | null
  /** Manager only (D3). */
  partnerUserId?: string | null
}

/** One row of a client's change history (R13). */
export interface ClientChangeRow {
  id: string
  changedAt: string
  field: string
  oldValue: string | null
  newValue: string | null
  reason: string | null
  changedBy: string | null
}

export interface ProductReadiness {
  product: string
  ready: boolean
  missing: { field: string; label: string; rule: string }[]
}

export interface DuplicateMatch {
  id: string
  clientNumber: number
  shortName: string
  registeredName: string | null
  matchedOn: ('registrationNumber' | 'vatNumber' | 'registeredName' | 'idNumber')[]
}

/** A column meaning the import knows (server/src/import-mapping.ts). */
export interface ImportConcept {
  key: string
  label: string
  group: string
  multiple?: boolean
  valueMapped?: boolean
  hint?: string
}

export interface ImportAnalysis {
  kind: 'clients' | 'services'
  headers: string[]
  rowCount: number
  repeatedHeaderRows: number
  delimiter: string
  sample: string[][]
  columnMap: string[]
  confidence: 'high' | 'low'
  hint: string
  concepts: ImportConcept[]
}

export interface ImportRequest {
  text: string
  columnMap: string[]
  valueMap?: Record<string, Record<string, string>>
  options?: { dropEmailDomains?: string[]; suggestEntityTypes?: boolean; obligationsFrom?: string }
  services?: { text: string; columnMap: string[]; serviceMap?: Record<string, string> } | null
}

/** One row of an import preview (server/src/client-import.ts). */
export interface ImportRow {
  row: number
  shortName: string
  action: 'create' | 'fill' | 'skip' | 'error'
  problems: string[]
  warnings: string[]
  notes: string[]
  fills: string[]
  existing: { id: string; clientNumber: number } | null
  work: string[]
}

export interface ImportValueChoice {
  raw: string
  rows: number
  target: string | null
  guessed: boolean
}

export interface ImportServicesSummary {
  services: { key: string; label: string; rows: number; target: string | null; guessed: boolean }[]
  targets: [string, string][]
  unmappedServices: number
  planned: { kind: 'obligation' | 'job'; code: string; rows: number; hasDueDates: boolean }[]
  notImported: { key: string; rows: number }[]
  incomeTaxWithoutEntity: number
  initials: { initials: string; rows: number; heldBy: string | null }[]
  linked: {
    byId: number
    byName: number
    unlinkedRows: number[]
    ambiguousNames: number
    servicesRows: number
    repeatedHeaderRows: number
    idsAdopted: number
  } | null
}

export interface ImportPreview {
  layout: string
  rows: ImportRow[]
  counts: { create: number; fill: number; skip: number; error: number }
  values: Record<string, ImportValueChoice[]>
  valueTargets: Record<string, [string, string][]>
  unmappedValues: number
  emailDomains: { domain: string; rows: number; dropped: boolean }[]
  services: ImportServicesSummary | null
  issues: { message: string; rows: number[] }[]
  obligationsFrom: string
}

export interface ImportResult {
  alreadyImported: boolean
  created: { row: number; id: string; clientNumber: number; shortName: string }[]
  filled: { row: number; id: string; clientNumber: number; fields: string[] }[]
  skipped: number
  groupsCreated: number
  obligationsCreated: number
  jobsCreated: number
  periods: {
    years: number[]
    created: number
    dueDatesSet: number
    refused: { obligationTypeCode: string; reason: string; count: number }[]
    error: string | null
  } | null
}

export interface ClientListParams {
  q?: string | null
  status?: string | null
  entityType?: string | null
  vat?: string | null
  yearEnd?: string | null
  sort?: string | null
  dir?: string | null
}

export interface ClientGroupRow {
  id: string
  name: string
  groupNumber: string | null
  _count: { clients: number }
}

export interface VatCategoryRow {
  code: string
  description: string
  filingFrequency: string | null
}

// -- User access (R14, R18) --------------------------------------------------

export type AccessRole = 'staff' | 'manager'

/** R18: the products a roster row can name. Accounts and Payroll have one role, `user`. */
export type AccessProduct = 'practice' | 'accounts' | 'payroll'

export interface ProductGrant {
  role: string
  grantedAt: string
  grantedBy: string
}

export interface OfferedProduct {
  id: AccessProduct
  label: string
  roles: string[]
}

export interface PersonAccess {
  userId: string
  email: string
  name: string
  createdDate: string
  lastSeenAt: string | null
  initials: string | null
  /** active = a live grant in any product. */
  status: 'active' | 'requesting' | 'revoked'
  /** The live Practice role, or null without Practice. */
  role: AccessRole | null
  products: Partial<Record<AccessProduct, ProductGrant>>
  grantedAt: string | null
  grantedBy: string | null
  revokedAt: string | null
  revokedBy: string | null
}

export interface AccessInvitation {
  id: string
  email: string
  product: AccessProduct
  role: string
  invitedAt: string
  invitedBy: string | null
  expiresAt: string
  expired: boolean
}

export interface AccessEvent {
  at: string
  email: string
  name: string
  what: string
  by: string | null
  note: string | null
}

export interface UserAccessOverview {
  people: PersonAccess[]
  invitations: AccessInvitation[]
  history: AccessEvent[]
  invitationDays: number
  /** The products this practice holds, Practice first. */
  products: OfferedProduct[]
  /** The signed-in manager's own user id -- their row cannot be changed here. */
  you: string
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Time & Billing, T&B-1 PR 1 -- billing jobs, rates, hours, client prices
// ---------------------------------------------------------------------------
// Amounts arrive as integer cents and leave as the rand text a person typed;
// hours arrive as decimal-hour text ("7.5"). The server does all the
// arithmetic and refuses what is not exact.

export type BillingJobKind = 'work' | 'leave'

export interface BillingTask {
  id: string
  name: string
  archived: boolean
}

export interface BillingJob {
  id: string
  name: string
  description: string | null
  kind: BillingJobKind
  chargeable: boolean
  obligationTypeCode: string | null
  archived: boolean
  tasks: BillingTask[]
}

export interface RateRow {
  id: string
  effectiveFrom: string
  costRateCents: number
  chargeOutRateCents: number
  note: string | null
  recordedAt: string
  superseded: boolean
}

export interface PatternRow {
  id: string
  effectiveFrom: string
  hours: [string, string, string, string, string, string, string]
  weeklyHours: string
  note: string | null
  recordedAt: string
  superseded: boolean
}

export interface PersonTimeSetup {
  person: StaffMember
  rateToday: RateRow | null
  rates: RateRow[]
  patternToday: PatternRow | null
  patterns: PatternRow[]
}

export type PriceBasis = 'fixed' | 'hourly'
export type PriceFrequency = 'monthly' | 'bi_monthly' | 'annual' | 'once_off'

export interface ClientJobRow {
  id: string
  basis: PriceBasis
  priceCents: number | null
  frequency: PriceFrequency | null
  effectiveFrom: string
  effectiveTo: string | null
  note: string | null
  recordedAt: string
  superseded: boolean
}

export interface ClientJobArrangement {
  job: { id: string; name: string; chargeable: boolean; archived: boolean }
  today: ClientJobRow | null
  upcoming: ClientJobRow | null
  history: ClientJobRow[]
}

export interface ClientJobsResponse {
  today: string
  client: { id: string; clientNumber: number; shortName: string }
  arrangements: ClientJobArrangement[]
}

export interface TimeSettingRow {
  id: string
  dueWeekday: number
  dueTime: string
  timerRoundingHundredths: number
  note: string | null
  effectiveAt: string
}

export interface TimeSettingsResponse {
  current: TimeSettingRow | null
  history: TimeSettingRow[]
}


// ---------------------------------------------------------------------------
// Time & Billing, T&B-1 PR 2 -- capture: entries, leave, disbursements, timer
// ---------------------------------------------------------------------------

export interface ClientRef {
  id: string
  clientNumber: number
  shortName: string
}

export interface TimeEntryRow {
  id: string
  workDate: string
  hours: string
  hundredths: number
  client: ClientRef | null
  job: { id: string; name: string; kind: BillingJobKind; chargeable: boolean }
  task: { id: string; name: string } | null
  narrative: string | null
  source: 'manual' | 'timer' | 'suggested'
  status: string
  timer: { startedAt: string; stoppedAt: string; seconds: number } | null
  /** While returned (PR 3): the manager's reason. */
  returnReason: string | null
}

export interface DisbursementRow {
  id: string
  incurredDate: string
  amountCents: number
  client: ClientRef
  description: string
  status: string
  returnReason: string | null
}

export interface TimerState {
  running: boolean
  elapsedSeconds: number
  wouldLog: string
  startedAt: string
  client: ClientRef | null
  job: { id: string; name: string; chargeable: boolean }
  task: { id: string; name: string } | null
  narrative: string | null
}

export interface WeekDay {
  date: string
  expected: string | null
  expectedHundredths: number | null
  holiday: string | null
  logged: string
  chargeable: string
  leave: string
}

export interface WeekResponse {
  weekStart: string
  weekEnd: string
  today: string
  days: WeekDay[]
  totals: { expected: string | null; logged: string; chargeable: string; leave: string }
  patternMissing: boolean
  entries: TimeEntryRow[]
  disbursements: DisbursementRow[]
  timer: TimerState | null
  /** T&B-1 PR 4: your weeks past due and not submitted (a reminder, decision 7). */
  overdueWeeks: { weekStart: string; dueAt: string }[]
  /** T&B-1 PR 3. */
  submission: {
    submittedAt: string[]
    toSubmit: number
    returned: number
    closedMonths: string[]
    canSubmit: boolean
  }
}

// ---------------------------------------------------------------------------
// Time & Billing, T&B-1 PR 4 -- reports
// ---------------------------------------------------------------------------

export const AGE_BANDS = ['Current', '30', '60', '90', '120+'] as const
export type AgeBand = (typeof AGE_BANDS)[number]

export interface ReportPeriod {
  start: string
  end: string | null
  label: string
}

export interface WipLine {
  kind: 'fixed' | 'hourly' | 'unpriced' | 'disbursement'
  client: ClientRef
  job: { id: string; name: string } | null
  label: string
  date: string
  people: string[]
  personId: string | null
  hours: string | null
  hundredths: number
  valueCents: number
  wipCents: number
  band: AgeBand
  detail: string | null
}

// -- T&B-1 PR 5: suggestions and Client time ---------------------------------

export type SuggestionEvent = 'for_review' | 'completed' | 'note'

export interface Suggestion {
  /** `period:<id>:<date>` -- what accepting or dismissing names. */
  key: string
  date: string
  client: ClientRef
  obligationType: { code: string; description: string }
  period: { id: string; start: string; end: string; label: string }
  events: SuggestionEvent[]
  notes: number
  /** Live work jobs linked to the obligation type; choose one when there are several. */
  jobs: { id: string; name: string; chargeable: boolean }[]
  narrative: string
}

export interface ClientTimeEntry {
  id: string
  person: { id: string; name: string }
  workDate: string
  hours: string
  hundredths: number
  job: { id: string; name: string; chargeable: boolean }
  task: string | null
  narrative: string | null
  status: string
  source: string
  /** Managers only; null for staff and for time not approved. */
  valueCents: number | null
}

export interface ClientTime {
  client: ClientRef
  from: string
  to: string
  scope: 'all' | 'own'
  entries: ClientTimeEntry[]
  disbursements: { id: string; person: { id: string; name: string }; incurredDate: string; amountCents: number; description: string; status: string }[]
  totals: { hours: string; approvedHours: string; valueCents: number | null }
  wip: WipReport | null
  recovery: RecoveryReport | null
}

export interface WipReport {
  asAt: string
  lines: WipLine[]
  totals: { wipCents: number; byBand: Record<AgeBand, number>; byKind: Record<WipLine['kind'], number> }
  unpricedCents: number
}

export interface FixedPeriodRow {
  client: ClientRef
  job: { id: string; name: string }
  period: ReportPeriod
  ended: boolean
  priceCents: number
  hours: string
  hundredths: number
  valueCents: number
  costCents: number
  writeOffCents: number
  gainCents: number
  recoveryBp: number | null
  marginCents: number
  firstWorkDate: string
  people: string[]
}

export interface RecoveryReport {
  from: string
  to: string
  periods: FixedPeriodRow[]
  totals: { priceCents: number; valueCents: number; costCents: number; writeOffCents: number; gainCents: number; marginCents: number; recoveryBp: number | null }
  hourly: { hours: string; valueCents: number; unpricedValueCents: number }
}

export interface ProductivitySpan {
  label: string
  from: string
  to: string
  expected: number | null
  leave: number
  available: number | null
  chargeable: number
  nonChargeable: number
  pending: number
  productivityBp: number | null
}

export interface ProductivityReport {
  month: string
  through: string
  people: { person: { id: string; name: string }; weeks: ProductivitySpan[]; month: ProductivitySpan }[]
}

export interface LateWeek {
  person: { id: string; name: string }
  weekStart: string
  dueAt: string
  submittedAt: string | null
  status: 'on_time' | 'late' | 'outstanding'
  lateMinutes: number
  hours: string
  items: number
}

export interface LateReport {
  month: string
  now: string
  weeks: LateWeek[]
  notAssessed: { id: string; name: string }[]
  /** The day of the practice's first timesheet submission; weeks due before it are not assessed. */
  startsFrom: string | null
}

// ---------------------------------------------------------------------------
// Time & Billing, T&B-1 PR 3 -- approval
// ---------------------------------------------------------------------------

export interface QueueEntry {
  id: string
  workDate: string
  hours: string
  hundredths: number
  client: ClientRef | null
  job: { id: string; name: string; kind: string; chargeable: boolean }
  task: { id: string; name: string } | null
  narrative: string | null
  source: string
  submittedAt: string
  wouldValueCents: number | null
  chargeRateCents: number | null
}

export interface QueueDisbursement {
  id: string
  incurredDate: string
  amountCents: number
  client: ClientRef
  description: string
  submittedAt: string
}

export interface QueueWeek {
  person: { id: string; name: string }
  weekStart: string
  ownTime: boolean
  hours: string
  wouldValueCents: number
  rateMissing: number
  entries: QueueEntry[]
  disbursements: QueueDisbursement[]
}

export interface StepResult {
  entries: number
  disbursements: number
  selfApproved: number
}

export interface MonthRow {
  month: string
  label: string
  closed: boolean
  last: { action: 'closed' | 'reopened'; at: string; by: string; reason: string | null } | null
  counts: { draft: number; submitted: number; returned: number; approved: number }
  ended: boolean
  canClose: boolean
}

export interface SelfApprovedItem {
  kind: 'entry' | 'disbursement'
  id: string
  date: string
  approvedAt: string
  hours: string | null
  valueCents: number
  client: ClientRef | null
  what: string
  waiting: boolean
}

export interface SelfApprovedGroup {
  approver: { id: string; name: string }
  month: string
  label: string
  hours: string
  valueCents: number
  items: SelfApprovedItem[]
  waiting: number
  signoffs: { reviewer: string; signedAt: string; itemCount: number; note: string | null }[]
  maySignOff: boolean
}

export interface EntryBody {
  workDate?: string
  hours?: string
  jobId?: string
  taskId?: string | null
  clientId?: string | null
  narrative?: string | null
}

export const api = {
  // -- Time & Billing capture (T&B-1 PR 2) ------------------------------------

  timeWeek: (start?: string) => request<WeekResponse>(`/api/time/week${query({ start })}`),

  createTimeEntry: (body: EntryBody) =>
    request<TimeEntryRow>('/api/time/entries', { method: 'POST', body: JSON.stringify(body) }),

  updateTimeEntry: (id: string, body: EntryBody) =>
    request<TimeEntryRow>(`/api/time/entries/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),

  deleteTimeEntry: (id: string) =>
    request<{ id: string }>(`/api/time/entries/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  createDisbursement: (body: { incurredDate: string; amount: string; clientId: string; description: string }) =>
    request<DisbursementRow>('/api/time/disbursements', { method: 'POST', body: JSON.stringify(body) }),

  updateDisbursement: (id: string, body: { incurredDate?: string; amount?: string; clientId?: string; description?: string }) =>
    request<DisbursementRow>(`/api/time/disbursements/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),

  deleteDisbursement: (id: string) =>
    request<{ id: string }>(`/api/time/disbursements/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  timer: () => request<{ timer: TimerState | null; available: boolean }>('/api/time/timer'),

  startTimer: (body: { jobId: string; taskId?: string | null; clientId?: string | null; narrative?: string | null }) =>
    request<{ timer: TimerState }>('/api/time/timer/start', { method: 'POST', body: JSON.stringify(body) }),

  updateTimer: (body: { jobId?: string; taskId?: string | null; clientId?: string | null; narrative?: string | null }) =>
    request<{ timer: TimerState }>('/api/time/timer', { method: 'PATCH', body: JSON.stringify(body) }),

  pauseTimer: () => request<{ timer: TimerState }>('/api/time/timer/pause', { method: 'POST', body: '{}' }),

  resumeTimer: () => request<{ timer: TimerState }>('/api/time/timer/resume', { method: 'POST', body: '{}' }),

  stopTimer: () => request<{ entry: TimeEntryRow }>('/api/time/timer/stop', { method: 'POST', body: '{}' }),

  discardTimer: () => request<{ discarded: true }>('/api/time/timer/discard', { method: 'POST', body: '{}' }),

  // -- Time & Billing reports (T&B-1 PR 4) -----------------------------------

  wipReport: () => request<WipReport>('/api/time/reports/wip'),

  recoveryReport: (from?: string, to?: string) => request<RecoveryReport>(`/api/time/reports/recovery${query({ from, to })}`),

  productivityReport: (month?: string) => request<ProductivityReport>(`/api/time/reports/productivity${query({ month })}`),

  lateTimesheets: (month?: string) => request<LateReport>(`/api/time/reports/late-timesheets${query({ month })}`),

  // -- Suggestions and Client time (T&B-1 PR 5) ------------------------------

  suggestions: (from?: string, to?: string) =>
    request<{ from: string; to: string; suggestions: Suggestion[] }>(`/api/time/suggestions${query({ from, to })}`),

  acceptSuggestion: (body: { key: string; hours: string; jobId?: string; taskId?: string | null; narrative?: string }) =>
    request<TimeEntryRow>('/api/time/suggestions/accept', { method: 'POST', body: JSON.stringify(body) }),

  dismissSuggestion: (key: string) =>
    request<{ key: string }>('/api/time/suggestions/dismiss', { method: 'POST', body: JSON.stringify({ key }) }),

  clientTime: (clientId: string, from?: string, to?: string) =>
    request<ClientTime>(`/api/clients/${encodeURIComponent(clientId)}/time${query({ from, to })}`),

  // -- Time & Billing approval (T&B-1 PR 3) ----------------------------------

  submitWeek: (weekStart: string) =>
    request<{ weekStart: string; submittedAt: string; entries: number; disbursements: number }>('/api/time/week/submit', {
      method: 'POST',
      body: JSON.stringify({ weekStart }),
    }),

  approvals: () => request<{ weeks: QueueWeek[] }>('/api/time/approvals'),

  approve: (body: { entryIds?: string[]; disbursementIds?: string[] }) =>
    request<StepResult>('/api/time/approvals/approve', { method: 'POST', body: JSON.stringify(body) }),

  returnToAuthor: (body: { entryIds?: string[]; disbursementIds?: string[]; reason: string }) =>
    request<StepResult>('/api/time/approvals/return', { method: 'POST', body: JSON.stringify(body) }),

  timeMonths: () => request<{ months: MonthRow[] }>('/api/time/months'),

  closeMonth: (month: string) =>
    request<{ months: MonthRow[] }>(`/api/time/months/${encodeURIComponent(month.slice(0, 7))}/close`, { method: 'POST', body: '{}' }),

  reopenMonth: (month: string, reason: string) =>
    request<{ months: MonthRow[] }>(`/api/time/months/${encodeURIComponent(month.slice(0, 7))}/reopen`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  selfApproved: () => request<{ groups: SelfApprovedGroup[] }>('/api/time/self-approved'),

  signOffSelfApproved: (body: { approverId: string; month: string; note?: string }) =>
    request<{ itemCount: number; signedAt: string }>('/api/time/self-approved/sign-off', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // -- Time & Billing (T&B-1) -------------------------------------------------

  billingJobs: () => request<{ jobs: BillingJob[] }>('/api/time/billing-jobs'),

  createBillingJob: (body: {
    name: string
    kind: BillingJobKind
    chargeable?: boolean
    obligationTypeCode?: string | null
    description?: string | null
  }) => request<BillingJob>('/api/time/billing-jobs', { method: 'POST', body: JSON.stringify(body) }),

  updateBillingJob: (id: string, patch: { name?: string; description?: string | null; obligationTypeCode?: string | null }) =>
    request<BillingJob>(`/api/time/billing-jobs/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  archiveBillingJob: (id: string) =>
    request<BillingJob>(`/api/time/billing-jobs/${encodeURIComponent(id)}/archive`, { method: 'POST', body: '{}' }),

  addBillingTask: (jobId: string, name: string) =>
    request<BillingJob>(`/api/time/billing-jobs/${encodeURIComponent(jobId)}/tasks`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  renameBillingTask: (id: string, name: string) =>
    request<BillingJob>(`/api/time/tasks/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ name }) }),

  archiveBillingTask: (id: string) =>
    request<BillingJob>(`/api/time/tasks/${encodeURIComponent(id)}/archive`, { method: 'POST', body: '{}' }),

  timePeople: () => request<{ today: string; people: PersonTimeSetup[] }>('/api/time/people'),

  recordRate: (body: { userId: string; effectiveFrom: string; costRate: string; chargeOutRate: string; note?: string }) =>
    request<{ id: string }>('/api/time/rates', { method: 'POST', body: JSON.stringify(body) }),

  recordWorkPattern: (body: { userId: string; effectiveFrom: string; hours: string[]; note?: string }) =>
    request<{ id: string }>('/api/time/work-patterns', { method: 'POST', body: JSON.stringify(body) }),

  clientBillingJobs: (clientId: string) =>
    request<ClientJobsResponse>(`/api/clients/${encodeURIComponent(clientId)}/billing-jobs`),

  recordClientJob: (
    clientId: string,
    body: { jobId: string; basis: PriceBasis; price?: string; frequency?: PriceFrequency; effectiveFrom: string; note?: string }
  ) =>
    request<{ id: string }>(`/api/clients/${encodeURIComponent(clientId)}/billing-jobs`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  endClientJob: (id: string, effectiveTo: string) =>
    request<{ id: string }>(`/api/time/client-jobs/${encodeURIComponent(id)}/end`, {
      method: 'POST',
      body: JSON.stringify({ effectiveTo }),
    }),

  timeSettings: () => request<TimeSettingsResponse>('/api/time/settings'),

  recordTimeSettings: (body: { dueWeekday: number; dueTime: string; timerRoundingHundredths: number; note?: string }) =>
    request<TimeSettingsResponse>('/api/time/settings', { method: 'POST', body: JSON.stringify(body) }),

  me: () => request<AuthState>('/api/auth/me'),

  userAccess: () => request<UserAccessOverview>('/api/users'),

  setUserAccess: (userId: string, role: string, note?: string, product: AccessProduct = 'practice') =>
    request<{ changed: boolean }>(`/api/users/${encodeURIComponent(userId)}/access`, {
      method: 'PUT',
      body: JSON.stringify({ role, note, product }),
    }),

  /** product 'all' ends every product in one transaction. */
  revokeUserAccess: (userId: string, note?: string, product: AccessProduct | 'all' = 'practice') =>
    request<{ changed: boolean }>(`/api/users/${encodeURIComponent(userId)}/access`, {
      method: 'DELETE',
      body: JSON.stringify({ note, product }),
    }),

  inviteUser: (email: string, grants: Partial<Record<AccessProduct, string>>, note?: string) =>
    request<{
      outcome: 'invited' | 'granted' | 'already_has_access'
      products: Partial<Record<AccessProduct, 'invited' | 'granted' | 'already_has_access'>>
    }>('/api/users/invitations', {
      method: 'POST',
      body: JSON.stringify({ email, grants, note }),
    }),

  cancelInvitation: (id: string) =>
    request<{ changed: boolean }>(`/api/users/invitations/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  clients: (params: ClientListParams) =>
    request<ClientListResponse>(`/api/clients${query({ ...params })}`),

  /** Not fetched -- a plain link, so the browser downloads it. */
  clientsCsvUrl: (params: ClientListParams) => `/api/clients.csv${query({ ...params })}`,

  importTemplateUrl: '/api/clients/import/template.csv',

  importAnalyse: (text: string, kind: 'clients' | 'services') =>
    request<ImportAnalysis>('/api/clients/import/analyse', { method: 'POST', body: JSON.stringify({ text, kind }) }),

  importPreview: (req: ImportRequest) =>
    request<ImportPreview>('/api/clients/import/preview', { method: 'POST', body: JSON.stringify(req) }),

  importCommit: (req: ImportRequest, importId: string, exclude: number[]) =>
    request<ImportResult>('/api/clients/import/commit', {
      method: 'POST',
      body: JSON.stringify({ ...req, importId, exclude }),
    }),

  createClient: (body: ClientPatch & { id: string; shortName: string }) =>
    request<ClientDetail>('/api/clients', { method: 'POST', body: JSON.stringify(body) }),

  duplicates: (params: {
    registrationNumber?: string | null
    vatNumber?: string | null
    registeredName?: string | null
    excludeId?: string | null
  }) => request<{ matches: DuplicateMatch[] }>(`/api/clients/duplicates${query(params)}`),

  clientChanges: (id: string) =>
    request<{ changes: ClientChangeRow[] }>(`/api/clients/${encodeURIComponent(id)}/changes`),

  clientReadiness: (id: string) =>
    request<{ products: ProductReadiness[] }>(`/api/clients/${encodeURIComponent(id)}/readiness`),

  addContact: (
    id: string,
    body: { fullName: string; email?: string | null; phone?: string | null; role?: string | null; isPrimary?: boolean }
  ) =>
    request<ClientDetail>(`/api/clients/${encodeURIComponent(id)}/contacts`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  removeContact: (id: string, linkId: string) =>
    request<ClientDetail>(
      `/api/clients/${encodeURIComponent(id)}/contacts/${encodeURIComponent(linkId)}`,
      { method: 'DELETE' }
    ),

  client: (id: string) => request<ClientDetail>(`/api/clients/${encodeURIComponent(id)}`),

  clientGroups: () => request<{ groups: ClientGroupRow[] }>('/api/client-groups'),

  vatCategories: () =>
    request<{ vatCategories: VatCategoryRow[] }>('/api/vat-categories'),

  updateClient: (id: string, patch: ClientPatch, reason?: string | null) =>
    request<ClientDetail>(`/api/clients/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(reason ? { ...patch, reason } : patch),
    }),

  obligationTypes: () =>
    request<{ obligationTypes: ObligationTypeInUse[] }>('/api/obligation-types'),

  dueAndOverdue: (params: {
    asOf?: string
    overdueOnly?: boolean
    assignedTo?: string
    allocatedTo?: string
    /** A number of days, or 'all'. */
    withinDays?: string
    /** 'YYYY-MM-DD': only items due strictly before this date. */
    dueBefore?: string
  }) =>
    request<DueAndOverdueResponse>(
      `/api/due-and-overdue${query({
        asOf: params.asOf,
        overdueOnly: params.overdueOnly ? '1' : undefined,
        assignedTo: params.assignedTo,
        allocatedTo: params.allocatedTo,
        withinDays: params.withinDays,
        dueBefore: params.dueBefore,
      })}`
    ),

  /** Manager only. A reason is required; see server/src/bulk.ts for the rules. */
  bulkSetPeriodStatus: (periodIds: string[], status: 'completed' | 'not_applicable', reason: string) =>
    request<BulkResult>('/api/obligation-periods/bulk-status', {
      method: 'POST',
      body: JSON.stringify({ periodIds, status, reason }),
    }),

  matrix: (
    code: string,
    params: {
      category?: string | null
      q?: string | null
      assignedTo?: string | null
      allocatedTo?: string | null
      status?: string | null
      periods?: 'recent' | 'all'
      asOf?: string
    }
  ) =>
    request<MatrixResponse>(
      `/api/matrix/${encodeURIComponent(code)}${query({
        category: params.category,
        q: params.q,
        assignedTo: params.assignedTo,
        allocatedTo: params.allocatedTo,
        status: params.status,
        periods: params.periods === 'all' ? 'all' : undefined,
        asOf: params.asOf,
      })}`
    ),

  /** `reason` is required for not_applicable (D7). */
  setObligationPeriodStatus: (id: string, status: string, reason?: string) =>
    request<{ status: string; nextStatuses: string[] }>(
      `/api/obligation-periods/${id}/status`,
      { method: 'POST', body: JSON.stringify({ status, reason }) }
    ),

  // -- Allocation and obligations (D3, P0-5, P0-6) --------------------------

  staff: () => request<{ staff: StaffMember[] }>('/api/staff'),

  assignableObligationTypes: () =>
    request<{ obligationTypes: { code: string; description: string }[] }>(
      '/api/obligation-types/assignable'
    ),

  clientObligations: (clientId: string) =>
    request<{ obligations: ClientObligation[] }>(
      `/api/clients/${encodeURIComponent(clientId)}/obligations`
    ),

  addObligation: (
    clientId: string,
    body: { obligationTypeCode: string; activeFrom: string; assignedUserId?: string | null }
  ) =>
    request<{ obligationId: string; ensure: EnsureResult }>(
      `/api/clients/${encodeURIComponent(clientId)}/obligations`,
      { method: 'POST', body: JSON.stringify(body) }
    ),

  endObligation: (obligationId: string, activeTo: string) =>
    request<{ periodsMarkedNotApplicable: number; periodsLeftForReview: number }>(
      `/api/obligations/${encodeURIComponent(obligationId)}/end`,
      { method: 'POST', body: JSON.stringify({ activeTo }) }
    ),

  allocateObligation: (obligationId: string, userId: string | null) =>
    request<{ ok: true }>(`/api/obligations/${encodeURIComponent(obligationId)}/allocation`, {
      method: 'PUT',
      body: JSON.stringify({ userId }),
    }),

  allocatePeriod: (periodId: string, userId: string | null) =>
    request<{ ok: true }>(`/api/obligation-periods/${encodeURIComponent(periodId)}/allocation`, {
      method: 'PUT',
      body: JSON.stringify({ userId }),
    }),

  setInitials: (userId: string, initials: string | null) =>
    request<{ ok: true }>(`/api/users/${encodeURIComponent(userId)}/initials`, {
      method: 'PUT',
      body: JSON.stringify({ initials }),
    }),

  ensurePeriods: () => request<EnsureResult>('/api/periods/ensure', { method: 'POST', body: '{}' }),

  setJobStatus: (id: string, status: string) =>
    request<{ status: string; nextStatuses: string[] }>(`/api/jobs/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    }),

  tickCoidaMilestone: (id: string, milestone: string) =>
    request<{ milestone: string; label: string }>(
      `/api/obligation-periods/${id}/coida-milestone`,
      { method: 'POST', body: JSON.stringify({ milestone }) }
    ),

  notes: (id: string) => request<NotesResponse>(`/api/obligation-periods/${id}/notes`),

  /**
   * `author` is ignored by the server when someone is signed in -- the note is
   * attributed to them, not to whatever was typed. It is still sent because in
   * anonymous mode there is nothing else to record.
   */
  addNote: (id: string, author: string, body: string) =>
    request<{ id: string }>(`/api/obligation-periods/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ author, body }),
    }),
}
